import { useContext } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

import { StoreContext } from '../store';
import API_BASE_URL from '../constants/api';
import { LOG_ACTIVE } from '../constants/actions';
import urlWithQuery from '../util/http';

type CustomHeaders = { [key: string]: string; }

const defaultHeaders = (token: string): CustomHeaders => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

const RequestMethod = {
  GET: 'GET',
  POST: 'POST',
  DELETE: 'DELETE',
};


type RequestOptions = {
  method?: string;
  data?: { [key: string]: string | number; };
  headers?: CustomHeaders;
  signal?: AbortSignal;
}

interface IUseRequest {
  request: (path: string, requestOptions: RequestOptions) => Promise<Response>;
  requestMethod: {[key: string]: string;};
}

const useRequest = (): IUseRequest => {
  const { dispatch } = useContext(StoreContext);

  const request = async (
    path: string,
    {
      method = RequestMethod.GET, data = {}, headers = {}, signal,
    }: RequestOptions = {},
  ): Promise<Response> => {
    // Fetch the current auth session to get the access token
    let accessToken = '';

    try {
      const session = await fetchAuthSession();

      accessToken = session.tokens?.accessToken?.toString() || '';
    } catch (error) {
      // User is not authenticated, proceed without token
      console.warn('Unable to fetch auth session:', error); // eslint-disable-line no-console
    }

    let url = `${API_BASE_URL}${path}`;

    const requestOptions: RequestInit = { // eslint-disable-line no-undef
      method,
      headers: { ...defaultHeaders(accessToken), ...headers },
      mode: 'cors',
      credentials: 'include',
    };

    if (signal) { requestOptions.signal = signal; }

    if (method === RequestMethod.GET) {
      url = urlWithQuery(url, data).toString();
    } else {
      requestOptions.body = JSON.stringify(data);
    }

    if (dispatch) {
      dispatch({ type: LOG_ACTIVE, payload: { time: Date.now() } });
    }

    return fetch(url, requestOptions);
  };

  return { request, requestMethod: RequestMethod };
};

export default useRequest;
